import { notEmpty } from '@eturi/util'
import { createSelector } from '@reduxjs/toolkit'
import {
	getFeatureRequirementResults,
	hasAndroidRestrixCapableDevice,
	hasFeatureCapableAndroidDevice,
	hasFeatureCapableDevice,
	hasFeatureCapableIOSDevice,
	mapRawToDevice,
} from '../model-util/index.js'
import { accountAccess$, activeChildId$, rawManagedDevices$ } from '../reducers/index.js'
import type { FeatureRequirement, ManagedDevice } from '../types/index.js'

const mapDevicesToUser = (userId: string, devices: ManagedDevice[]) =>
	devices.filter((d) => d.users?.includes(userId))

export const managedDevices$ = /*@__PURE__*/ createSelector(
	rawManagedDevices$,
	(d): ManagedDevice[] => d.map(mapRawToDevice),
)

// Gets devices that have device location enabled
export const managedGeoDevices$ = /*@__PURE__*/ createSelector(managedDevices$, (dvs) =>
	dvs.filter((d) => d.device_location_active),
)

// "Valid" managed devices are for non-retired devices that have users
export const pairedManagedDevices$ = /*@__PURE__*/ createSelector(managedDevices$, (dvs) =>
	dvs.filter((d) => d.pairStatus != 'not_paired'),
)

// Returns devices that have not been retired
// FIXME: Why is this different from `validManagedDevices$`?
// export const nonRetiredManagedDevices$ = /*@__PURE__*/ createSelector(managedDevices$, (dvs) =>
// 	dvs.filter((d) => !d.retired && notEmpty(d.users)),
// )
// NOTE: For now I'm aliasing this, because I don't think they should be different. When I verify that
// they aren't, I will refactor accordingly.
// FIXME: Audit this. My guess is this is relevant to location history and view screenshots for
// devices that are not longer paired?
export const nonRetiredManagedDevices$ = pairedManagedDevices$

export const hasPairedManagedDevice$ = /*@__PURE__*/ createSelector(pairedManagedDevices$, notEmpty)

// Determines whether user has reached max devices limit
export const hasMaxDevices$ = /*@__PURE__*/ createSelector(
	accountAccess$,
	pairedManagedDevices$,
	(aa, d) => d.length >= aa.devices_per_account,
)

// Filters on devices belonging to the active user, including retired.
export const activeUserDevicesAll$ = /*@__PURE__*/ createSelector(
	activeChildId$,
	managedDevices$,
	mapDevicesToUser,
)

// Filters on paired devices belonging to active user
export const activeUserPairedDevices$ = /*@__PURE__*/ createSelector(
	activeChildId$,
	pairedManagedDevices$,
	mapDevicesToUser,
)

export const activeUserHasPairedDevice$ = /*@__PURE__*/ createSelector(
	activeUserPairedDevices$,
	notEmpty,
)

export const activeUserFeatureRequirementResults$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	getFeatureRequirementResults,
)

export const activeUserHasFeatureCapableDevice$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	hasFeatureCapableDevice,
)

export const activeUserHasFeatureCapableAndroidDevice$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	hasFeatureCapableAndroidDevice,
)

export const activeUserHasFeatureCapableIOSDevice$ = createSelector(
	(_: unknown, featureRequirement: FeatureRequirement) => featureRequirement,
	activeUserPairedDevices$,
	hasFeatureCapableIOSDevice,
)

export const activeUserHasAndroidRestrixCapableDevice$ = createSelector(
	(_: unknown, minOSVer: number) => minOSVer,
	activeUserPairedDevices$,
	hasAndroidRestrixCapableDevice,
)

export const activeUserHasDevicePaired$ = /*@__PURE__*/ createSelector(
	activeChildId$,
	nonRetiredManagedDevices$,
	(activeUserId, nonRetiredDevices) => {
		if (!activeUserId) return false

		return nonRetiredDevices.some((d) => d.users.includes(activeUserId))
	},
)

export const pairedUserDevices$ = createSelector(
	pairedManagedDevices$,
	(_: unknown, userId: string) => userId,
	(devices, userId) => devices.filter((d) => d.users.includes(userId)),
)
